// firestoreRetry.js
//
// Guards against Firestore's "first query on a cold connection hangs
// forever" failure mode. On some networks (mobile carriers, proxies) and
// in PWA/service-worker contexts, the initial streaming connection can
// stall — and a one-shot getDocs()/getDoc() never rejects on its own, so a
// page whose spinner is gated on that read sits there indefinitely until
// the user manually navigates away and back (which re-issues the query on a
// now-warm connection).
//
// withRetry() races each attempt against a timeout and re-issues on
// timeout/error. IMPORTANT for cost: the success path runs the read exactly
// ONCE — no extra Firestore reads in steady state. Extra reads only occur
// when an attempt actually stalls/fails, and only for the small query it
// wraps. Pair this with experimentalAutoDetectLongPolling in firebase.js,
// which fixes the underlying connection stall; this is the belt-and-braces
// so a single hung query can never strand the UI again.

import { getDocsFromCache, getDocFromCache } from 'firebase/firestore';
import { reconnect } from '../connection';

// Cache-first read helpers. Return the snapshot straight from the persistent
// IndexedDB cache when it's there (for an INSTANT first paint on navigation),
// or null on a cache miss / disabled cache / any error. Callers paint from
// this immediately, then run the authoritative server read (via withRetry)
// and repaint. Never throw and never touch the network — a null just means
// "nothing cached yet, fall through to the server read." No extra billed
// reads: cache hits are local, and the server read happens either way.
export async function cachedDocs(q) {
  try {
    const snap = await getDocsFromCache(q);
    return snap.empty ? null : snap;
  } catch {
    return null;
  }
}

export async function cachedDoc(ref) {
  try {
    const snap = await getDocFromCache(ref);
    return snap.exists() ? snap : null;
  } catch {
    return null;
  }
}

/** Race a promise against a timeout (rejects with Error("firestore-timeout")). */
export function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('firestore-timeout')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// Run an async read with a per-attempt timeout, retrying on timeout/error.
//   fn        — () => Promise (e.g. () => getDocs(q))
//   attempts  — total tries before giving up (default 4)
//   timeoutMs — per-attempt timeout (default 6000)
// On a timeout we also kick the Firestore connection (reconnect) before the next
// try — the common cause of a stalled read is a dead long-poll after the PWA
// resumes, so passively re-issuing against the same dead connection wouldn't
// help; re-establishing it does. Rejects with the last error if all attempts fail.
export async function withRetry(fn, { attempts = 4, timeoutMs = 6000 } = {}) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await withTimeout(Promise.resolve(fn()), timeoutMs);
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) {
        // A timed-out read means the connection is likely dead — re-establish it,
        // then re-issue after a brief backoff.
        if (err && err.message === 'firestore-timeout') {
          try { await reconnect(); } catch { /* best-effort */ }
        }
        await new Promise((r) => setTimeout(r, 500 * (i + 1)));
      }
    }
  }
  throw lastErr;
}
